"use client";

import { AttendanceStatus } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRef, useState } from "react";
import { IoShareSocialOutline } from "react-icons/io5";
import {
    LuCheck,
    LuClipboard,
    LuCopy,
    LuExternalLink,
    LuImage,
    LuMessageSquare,
    LuPencil,
    LuUserPlus,
} from "react-icons/lu";
import {
    useEventContextV2,
    usePermissionsContext,
    useWhiteLabel,
} from "~/app/providers";

import { FaUndoAlt } from "react-icons/fa";
import { getProximityAndFollowersInvites } from "~/app/events/[slug]/invites/actions";
import ShareEventModal from "~/app/events/[slug]/share/_components/ShareEventModal";
import UploadImage from "~/components/file/UploadImage";
import { attachImage } from "~/components/v3/events-actions/images";
import { Button } from "~/components/v3/themed/Button";
import { AcceptType } from "~/utils/images";
import { GetEventFromSlug } from "../actions";
import { InvitesProvider } from "./contexts/InvitesContext";
import { EditEventSheet } from "./EditEventSheet";
import { EventInvitesModal } from "./EventInvitesModal";
import EventRecurrenceSheet from "./EventRecurrenceSheet";
import { toast } from "react-toastify";
import { cn } from "~/utils/fontsConfig";
import { CustomizableLayout } from "~/types/CustomizableLayout";

export const EventManageActions = ({
    event,
    customization,
}: {
    event: GetEventFromSlug;
    customization?: CustomizableLayout;
}) => {
    const permissions = usePermissionsContext();
    const [isEditEventSheetOpen, setIsEditEventSheetOpen] = useState(false);

    const isAllowedToEdit =
        permissions.isHost || permissions.isManager || permissions.isAdmin;

    if (!isAllowedToEdit) return null;

    const [isCopied, setIsCopied] = useState(false);

    // copy event link, show toast and set isCopied to true for 2 seconds
    const copyLink = () => {
        navigator.clipboard.writeText(
            `${window.location.origin}/beta/events/${event.slug}`,
        );
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 2000);
    };

    return (
        <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button
                variant="secondary"
                className={cn(
                    "flex items-center gap-2 text-sm",
                    customization?.buttons?.secondary,
                )}
                size="md"
                onClick={() => setIsEditEventSheetOpen(true)}
            >
                <LuPencil className="h-3 w-3" />
                <span>Edit event</span>
            </Button>

            <Link href={`/beta/events/${event.slug}`}>
                <Button
                    className={cn(
                        "flex items-center gap-2 text-sm",
                        customization?.buttons?.secondary,
                    )}
                    size="md"
                    variant="secondary"
                >
                    <LuExternalLink className="h-3 w-3 font-semibold" />
                    <span>Event Page</span>
                </Button>
            </Link>

            <Button
                variant="default"
                size="xs"
                className={cn(
                    "h-[28px] w-[104px] rounded-lg bg-white/15 p-3 text-xs text-white",
                    isCopied && "bg-v3-C500",
                    customization?.buttons?.secondary,
                )}
                onClick={copyLink}
            >
                {isCopied ? (
                    <>
                        <LuCheck className="h-4 w-4 fill-v3-C500" />
                        <span>Copied</span>
                    </>
                ) : (
                    <>
                        <LuCopy className="h-4 w-4" />
                        <span>Copy Link</span>
                    </>
                )}
            </Button>

            <EditEventSheet
                isOpen={isEditEventSheetOpen}
                setIsOpen={setIsEditEventSheetOpen}
                event={event}
                customization={customization}
            />
        </div>
    );
};

export const EventInviteActions = ({ className }: { className?: string }) => {
    const event = useEventContextV2();
    const permissions = usePermissionsContext();
    const session = useSession();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const customization = useWhiteLabel()?.customization;

    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [isRecurrenceSheetOpen, setIsRecurrenceSheetOpen] = useState(false);
    const [recurrenceMode, setRecurrenceMode] = useState<
        "recurrence" | "duplication"
    >("duplication");

    const isAllowed =
        permissions.isHost || permissions.isManager || permissions.isAdmin;

    const isAllowedToUploadPhotos =
        isAllowed ||
        event.attendees.some(
            (a) =>
                a.user.id === session?.data?.user?.id &&
                a.status === AttendanceStatus.ATTENDING,
        );

    const updateImageList = async (filename: string) => {
        // add to db for the event
        await attachImage(event.slug, filename);
    };

    return (
        <div className="grid grid-cols-1 gap-2 xs:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
            <InviteGuestsButton />

            <Button
                variant="default"
                size="xs"
                className={cn(
                    "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                    customization?.buttons?.secondary,
                )}
                onClick={() => setShareModalOpen(true)}
            >
                <IoShareSocialOutline className="h-3 w-3" />
                <span>Share Event</span>
            </Button>

            {isAllowed && (
                <>
                    <Button
                        variant="default"
                        size="xs"
                        className={cn(
                            "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                            customization?.buttons?.secondary,
                        )}
                        disabled={!isAllowed}
                    >
                        <Link
                            href={`/beta/events/${event.slug}/manage/messages`}
                            className="flex items-center gap-2"
                        >
                            <LuMessageSquare className="h-3 w-3" />
                            <span>Send a message</span>
                        </Link>
                    </Button>
                    <Button
                        variant="default"
                        size="xs"
                        className={cn(
                            "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                            customization?.buttons?.secondary,
                        )}
                        disabled={!isAllowed}
                        onClick={() => {
                            setRecurrenceMode("recurrence");
                            setIsRecurrenceSheetOpen(true);
                        }}
                    >
                        <FaUndoAlt className="h-3 w-3" />
                        <span>Recurrence</span>
                    </Button>
                    <Button
                        variant="default"
                        size="xs"
                        className={cn(
                            "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                            customization?.buttons?.secondary,
                        )}
                        disabled={!isAllowed}
                        onClick={() => {
                            setRecurrenceMode("duplication");
                            setIsRecurrenceSheetOpen(true);
                        }}
                    >
                        <LuCopy className="h-3 w-3" />
                        <span>Duplicate Event</span>
                    </Button>
                </>
            )}

            {isAllowedToUploadPhotos && (
                <Button
                    variant="default"
                    size="xs"
                    className={cn(
                        "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                        customization?.buttons?.secondary,
                    )}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!isAllowedToUploadPhotos}
                >
                    <LuImage className="h-3 w-3" />
                    <span>Upload photos</span>
                </Button>
            )}

            <EventRecurrenceSheet
                isOpen={isRecurrenceSheetOpen}
                onClose={() => setIsRecurrenceSheetOpen(false)}
                event={event}
                defaultMode={recurrenceMode}
            />

            <ShareEventModal
                isVisible={shareModalOpen}
                setIsVisible={setShareModalOpen}
                event={event}
            />
            <UploadImage
                className="hidden"
                ref={fileInputRef}
                variant="button"
                multi={true}
                bucketKey={`event/${event.id}`}
                onUploadComplete={updateImageList}
                accept={[AcceptType.VIDEO, AcceptType.IMAGE]}
            />
        </div>
    );
};

export const InviteGuestsButton = ({ refetch }: { refetch?: () => void }) => {
    const event = useEventContextV2();
    const permissions = usePermissionsContext();
    const [isInvitesModalOpen, setIsInvitesModalOpen] = useState(false);
    const customization = useWhiteLabel()?.customization;

    const { data: invitesData } = useQuery(
        ["proximity-and-followers-invites", event?.id],
        () =>
            getProximityAndFollowersInvites({
                params: {
                    communitySlug: event?.community?.slug,
                    eventSlug: event?.slug,
                    eventId: event?.id,
                },
            }),
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    );

    const isAllowed =
        permissions.isHost || permissions.isManager || permissions.isAdmin;

    return (
        <>
            {isAllowed && (
                <Button
                    variant="default"
                    size="xs"
                    className={cn(
                        "w-full rounded-lg bg-white/15 p-3 text-xs text-white",
                        customization?.buttons?.secondary,
                    )}
                    disabled={!isAllowed}
                    onClick={() => setIsInvitesModalOpen(true)}
                >
                    <LuUserPlus className="h-3 w-3" />
                    <span>Invite guests</span>
                </Button>
            )}

            <InvitesProvider>
                <EventInvitesModal
                    isOpen={isInvitesModalOpen}
                    onClose={() => setIsInvitesModalOpen(false)}
                    eventId={event.id}
                    refetch={refetch}
                    initialData={
                        invitesData ?? {
                            eligibleUsers: [],
                            inviteCount: 0,
                            followers: [],
                            invitedAttendees: [],
                        }
                    }
                />
            </InvitesProvider>
        </>
    );
};
